// ============================================================
// fondos/efectiva.js — LA CONFIGURACIÓN EFECTIVA de un fondo: lo que de verdad recibe (DOM: lee)
//
// Existe porque hubo DOS: la del runtime y la que el BG Studio se montaba por su cuenta para la
// vista previa. Diferían en tres cosas —el segundo color de marca (`--secondary`), el color de
// acento y la opacidad del overlay— así que lo que el cliente veía al ELEGIR no era lo que quedaba
// al Aplicar ni lo que salía publicado (medido: 39,7 niveles por canal de diferencia en un
// `gradient-flow`, y un secundario que pesaba 0 % en la vista previa y 49,5 % aplicado).
// Regla: el motor (`fondos/motor.js`) la llama para el runtime Y para la vista previa del panel;
// nadie compone la config a mano. Vivía en `animations/backgrounds.js` hasta el P3.
// ============================================================

import { isDarkBackground } from '../../utils/contrast.js';
import { MODO_SOBRE_FOTO, OPACIDAD_MAX_SOBRE_FOTO } from './capacidad.js';
import { esTexturaDeOverlay, esTexturaDeFondo, alfaDeTextura } from './texturas.js';
import { alfaMaxLegible, opacidadLegible, ALFA_MAX_SOBRE_FOTO } from './legible.js';
import { segundoColor } from './paleta.js';
import { rangoDe, opacidadPintada, idVigente } from './catalogo.js';

/**
 * Lee de la SECCIÓN (que es lo único que existe en las tres superficies) y admite un `override`
 * para la vista previa, que trabaja sobre un borrador que todavía no está en los props.
 *
 * @param {HTMLElement} section  - la <section> con data-bg-anim / data-bg-config / data-accent-color
 * @param {{tipo?: string, config?: Object, modo?: string, acento?: string, tono?: string}} [override]
 *   borrador del panel; `modo` es la capacidad del bloque (`fondos/capacidad.js`), que en la
 *   sección solo consta si ya llevaba un fondo guardado (`data-bg-modo`); `acento` y `tono`, lo que
 *   pondría la inyección (`fondos/inyectar.js::datosDeFondo`).
 * @returns {{tipo, color1, isDark, config, overlay, overlayColor, overlayOpacity}}
 */
export function configEfectiva(section, override) {
    // Lo publicado antes de retirar un fondo lleva su id horneado: se pinta como su sustituto.
    const tipo = idVigente((override && override.tipo !== undefined)
        ? override.tipo
        : (section.getAttribute('data-bg-anim') || 'none'));

    let bruta = (override && override.config !== undefined) ? override.config : null;
    if (bruta === null) {
        const attr = section.getAttribute('data-bg-config');
        if (attr) {
            try { bruta = JSON.parse(attr); }
            catch (e) { console.error('Error parsing data-bg-config:', e); }
        }
    }
    const config = Object.assign({}, bruta || {});

    // Acento: el atributo lo escriben render.js y compileSiteHtml desde props.accentColor, así que
    // vale igual en editor, «Ver sitio» y publicado. La vista previa del PRIMER fondo de un bloque
    // no lo tiene aún en la sección y lo trae en el override (`fondos/inyectar.js::datosDeFondo`).
    let color1 = config.color1 || (override && override.acento)
        || section.getAttribute('data-accent-color') || '#6366f1';

    // EL ACENTO SOBRE UNA SECCIÓN DEL COLOR DEL ACENTO NO SE VE (L10 del plan del motor de fondos:
    // banner v3 pinta la sección con `var(--accent)` y un `grid` del acento se quedaba en el 0 %
    // visible). Si el cliente no eligió color a mano y el acento cae encima del fondo, el fondo se
    // pinta con el color del TEXTO de la sección, que por construcción contrasta con ella.
    if (!config.color1) {
        const fondo = _fondoPropio(section) || _rgbDeColor(_varInline(section, '--bg'));
        const acento = _rgbDeColor(color1);
        if (fondo && acento && _distancia(fondo, acento) < DISTANCIA_ACENTO_INVISIBLE) {
            const texto = _rgbDeColor(_estilo(section, 'color'));
            if (texto && _distancia(texto, fondo) >= DISTANCIA_ACENTO_INVISIBLE) color1 = _aHex(texto);
        }
    }

    const isDark = _seccionOscura(section, override && override.tono);

    // DUOTONO DE MARCA (E4, `fondos/paleta.js`): varias animaciones aceptan un SEGUNDO color. El
    // secundario del cliente viaja como `--secondary` en el style INLINE de la <section> (lo inyectan
    // render.js y compileSiteHtml; se lee del inline para no heredar el de un ancestro), pero ahí va
    // CONTRASTADO para badges: en una sección clara, casi negro. La paleta le devuelve su tono de
    // decoración conservando el matiz; sin secundario, el acento en otro tono. Un color2 elegido a
    // mano en el panel MANDA.
    config.color2 = segundoColor({ c1: color1, c2Mano: config.color2, c2Marca: _varInline(section, '--secondary'),
                                   oscuro: isDark });

    // SIN OPACIDAD GUARDADA, la del CATÁLOGO para ese fondo (la segunda red de legibilidad del P3,
    // §2.5: la primera es el alfa acotado dentro del efecto). Las que la tienen a 100 no se tocan:
    // su capa se queda sin opacidad propia, como siempre.
    if (config.opacity === undefined || config.opacity === null) {
        const r = rangoDe(tipo, 'opacity');
        if (r && r.defecto < 100) config.opacity = r.defecto;
    }

    // LA CURVA de los fondos que la llevan (`catalogo.js::curvaOpacidad`, decisión del dueño 29-09): la
    // opacidad guardada se pinta por ella (45 → 67). ANTES del tope de la foto, que es un tope de lo
    // que se pinta.
    config.opacity = opacidadPintada(tipo, config.opacity);

    // SOBRE UNA FOTO, OPACIDAD ACOTADA (decisión del dueño Q2, 22-09-2026; `fondos/capacidad.js`).
    // `data-bg-modo` lo pone la inyección (render.js y compileSiteHtml) según la capacidad del
    // bloque; la vista previa del panel la pasa en el override. Sin opacidad propia se toma el
    // tope: al 100 % el fondo competiría con la foto, que es la protagonista.
    const modo = (override && override.modo !== undefined) ? override.modo : section.getAttribute('data-bg-modo');
    if (modo === MODO_SOBRE_FOTO) {
        const op = (config.opacity === undefined || config.opacity === null) ? 100 : Number(config.opacity);
        config.opacity = Math.min(Number.isFinite(op) ? op : 100, OPACIDAD_MAX_SOBRE_FOTO);
    }

    // Overlay: se normaliza contra el registro que lo pinta (`texturas.js`, con `hasOwnProperty`:
    // un id que el front no conoce —'glow', 'noise', 'constructor'— es 'none', mismo criterio que
    // `_OVERLAY_WHITELIST` en el backend).
    const overlay = esTexturaDeOverlay(config.overlay) ? config.overlay : 'none';
    const overlayColor = config.overlayColor || color1;
    // `undefined` = sin opacidad propia; el consumidor NO la toca (queda al 100 %). No se inventa
    // un valor por defecto aquí; solo el tope de legibilidad de abajo puede ponerle uno.
    let overlayOpacity = config.overlayOpacity;

    // EL TOPE DE LEGIBILIDAD de las texturas (03-10-2026, `fondos/legible.js`): una textura no
    // puede bajar el contraste del texto de la sección de 9:1, ni quitarle más de un 10 % si ya
    // estaba por debajo (`legible.js::SUELO`). Es un tope de lo que se PINTA, como el de la foto: la
    // opacidad guardada no cambia y el panel la sigue enseñando. Sobre una foto no hay un color de
    // fondo con el que medir: una textura de fondo ya va acotada por Q2 (arriba) y un overlay no
    // pasa del alfa que tenía antes del 01-10 (la foto conserva ≥ 89 % de su detalle).
    const sobreFoto = modo === MODO_SOBRE_FOTO;
    const fondoSec = sobreFoto ? null : _fondoDeLaSeccion(section);
    const textoSec = sobreFoto ? null : _rgbDeColor(_estilo(section, 'color'));
    if (esTexturaDeFondo(tipo) && !sobreFoto) {
        config.opacity = opacidadLegible(config.opacity, alfaDeTextura(tipo, isDark, false),
            alfaMaxLegible(fondoSec, textoSec, _rgbDeColor(color1)));
    }
    if (overlay !== 'none') {
        overlayOpacity = opacidadLegible(overlayOpacity, alfaDeTextura(overlay, isDark, true), sobreFoto
            ? ALFA_MAX_SOBRE_FOTO
            : alfaMaxLegible(fondoSec, textoSec, _rgbDeColor(overlayColor)));
    }

    return {
        tipo,
        color1,
        isDark,
        config,
        overlay,
        overlayColor,
        overlayOpacity,
    };
}

// ── El tono y el color de la sección sobre la que se pinta ────────────────────────────────────

/** Por debajo de esta distancia RGB (0-441) dos colores se confunden como fondo y figura. */
const DISTANCIA_ACENTO_INVISIBLE = 60;

function _estilo(el, prop) {
    try { return window.getComputedStyle(el)[prop] || ''; } catch (e) { return ''; }
}

function _varInline(el, nombre) {
    return ((el.style && el.style.getPropertyValue(nombre)) || '').trim();
}

/** `[r, g, b]` de un color `#rgb`, `#rrggbb`, `rgb()` o `rgba()`; null si no se puede leer o es
 *  casi transparente (alfa < 0,5: entonces no es "el color de la sección"). */
function _rgbDeColor(c) {
    const s = (c || '').trim();
    const m = s.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,/]+([\d.]+)(%?))?\s*\)$/i);
    if (m) {
        if (m[4] !== undefined) {
            const alfa = parseFloat(m[4]) / (m[5] === '%' ? 100 : 1);
            if (!(alfa >= 0.5)) return null;
        }
        return [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)];
    }
    const h = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!h) return null;
    const x = h[1].length === 3 ? h[1].replace(/./g, '$&$&') : h[1];
    return [parseInt(x.slice(0, 2), 16), parseInt(x.slice(2, 4), 16), parseInt(x.slice(4, 6), 16)];
}

function _aHex(rgb) {
    return '#' + rgb.map(function (v) { return ('0' + Math.max(0, Math.min(255, v)).toString(16)).slice(-2); }).join('');
}

function _distancia(a, b) {
    return Math.sqrt(Math.pow(a[0] - b[0], 2) + Math.pow(a[1] - b[1], 2) + Math.pow(a[2] - b[2], 2));
}

/** El color SÓLIDO que pinta la propia sección (no sus antepasados), o null. */
function _fondoPropio(section) {
    return _rgbDeColor(_estilo(section, 'backgroundColor'));
}

/**
 * El color sobre el que queda el texto de la sección, para el tope de legibilidad: el suyo, su
 * `--bg` o, si es transparente, el del primer antepasado que pinte uno (como `isDarkBackground`).
 * null si no hay ninguno: entonces no hay tope.
 */
function _fondoDeLaSeccion(section) {
    const propio = _fondoPropio(section) || _rgbDeColor(_varInline(section, '--bg'));
    if (propio) return propio;
    for (let n = section.parentElement; n; n = n.parentElement) {
        const c = _rgbDeColor(_estilo(n, 'backgroundColor'));
        if (c) return c;
    }
    return null;
}

/**
 * ¿Se pinta el fondo sobre OSCURO? (L12 del plan del motor de fondos.) Por orden:
 *   1. el tono que la inyección sacó del DATO del bloque (`data-bg-tono`, `fondos/inyectar.js`; la
 *      vista previa del panel lo pasa como `tonoOverride`). Manda sobre el color de la sección
 *      porque no siempre es lo que queda debajo: el banner v2 pinta `bgColor` y encima un velo
 *      negro al 55 %, y el vídeo no pinta color propio (subiendo por el DOM llegaba al envoltorio
 *      en el editor y al blanco de `#resultado` en el publicado: el mismo fondo, a media fuerza);
 *   2. lo que pinta la PROPIA sección, igual en las tres superficies;
 *   3. lo de siempre, subir por el DOM: solo para el HTML publicado antes de este cambio.
 */
function _seccionOscura(section, tonoOverride) {
    const tono = tonoOverride || section.getAttribute('data-bg-tono');
    if (tono === 'oscuro') return true;
    if (tono === 'claro') return false;
    const propio = _fondoPropio(section);
    if (propio) return (propio[0] * 299 + propio[1] * 587 + propio[2] * 114) / 1000 < 128;
    return isDarkBackground(section);
}
